import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Logo } from "@/components/common/Logo";
import { LogOut, X, Menu, Sun, Moon, LayoutDashboard } from "lucide-react";
import { MarketTicker } from "@/components/market/MarketTicker";

// Discord icon component
const DiscordIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

// Discord invite link - UPDATE THIS with your actual invite link
const DISCORD_INVITE_URL = "https://discord.gg/9XN8WpAgPW";

export const Header = ({ onCtaClick }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  
  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <MarketTicker />
      <div className="bg-slate-950/90 backdrop-blur-2xl border-b border-white/5">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        <Logo />
        <nav className="hidden md:flex items-center gap-3">
          {/* Page links */}
          <Link to="/futures-prop-firm-rules" className="text-xs text-slate-400 hover:text-white transition-colors font-medium" data-testid="nav-rules">Rules Hub</Link>
          <Link to="/community-pulse" className="text-xs text-slate-400 hover:text-white transition-colors font-medium" data-testid="nav-community-pulse">Community Pulse</Link>
          <Link to="/blog" className="text-xs text-slate-400 hover:text-white transition-colors font-medium" data-testid="nav-blog">Blog</Link>
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full transition-all text-slate-400 hover:text-white"
            data-testid="theme-toggle"
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          {/* Discord Join Button - Discord blue pill */}
          <a 
            href={DISCORD_INVITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs bg-[#5865F2] hover:bg-[#4752C4] text-white px-4 py-1.5 rounded-full transition-all font-semibold flex items-center gap-1.5 shadow-lg shadow-[#5865F2]/20"
            data-testid="nav-discord"
          >
            <DiscordIcon size={14} /> Join Discord
          </a>
          {user ? (
            <>
              <Link to="/dashboard" className="text-xs bg-[#2979FF] hover:bg-[#1565C0] text-white px-4 py-1.5 rounded-full transition-all font-semibold flex items-center gap-1.5 shadow-lg shadow-[#2979FF]/20" data-testid="nav-dashboard">
                <LayoutDashboard size={13} /> Dashboard
              </Link>
              <button onClick={logout} className="text-sm text-slate-500 hover:text-white transition-colors" data-testid="nav-logout">
                <LogOut size={16} />
              </button>
            </>
          ) : (
            <Link to="/signup" className="text-xs bg-[#31D65C] hover:bg-[#28c050] text-white px-4 py-1.5 rounded-full transition-all font-bold shadow-lg shadow-[#31D65C]/20" data-testid="nav-signup">
              Sign up
            </Link>
          )}
        </nav>
        <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden text-white p-2" data-testid="mobile-menu-toggle">
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {mobileOpen && (
        <div className="md:hidden bg-slate-950 border-t border-white/5 px-4 py-4 space-y-3" data-testid="mobile-menu">
          {/* Page Links - Mobile */}
          <Link to="/futures-prop-firm-rules" onClick={() => setMobileOpen(false)} className="block text-sm text-slate-400 hover:text-white py-2" data-testid="nav-rules-mobile">Rules Hub</Link>
          <Link to="/community-pulse" onClick={() => setMobileOpen(false)} className="block text-sm text-slate-400 hover:text-white py-2" data-testid="nav-community-pulse-mobile">Community Pulse</Link>
          <Link to="/blog" onClick={() => setMobileOpen(false)} className="block text-sm text-slate-400 hover:text-white py-2" data-testid="nav-blog-mobile">Blog</Link>
          {/* Theme Toggle - Mobile */}
          <button
            onClick={toggleTheme}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white py-2 w-full"
            data-testid="theme-toggle-mobile"
          >
            {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
            {theme === "dark" ? "Light Mode" : "Dark Mode"}
          </button>
          {/* Discord Join Button - Mobile */}
          <a 
            href={DISCORD_INVITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMobileOpen(false)}
            className="flex items-center justify-center gap-2 text-sm bg-[#5865F2] text-white px-5 py-2.5 rounded-full font-semibold shadow-lg shadow-[#5865F2]/20"
          >
            <DiscordIcon size={16} /> Join Discord
          </a>
          {user ? (
            <>
              <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="flex items-center justify-center gap-2 text-sm bg-[#2979FF] text-white px-5 py-2.5 rounded-full font-semibold shadow-lg shadow-[#2979FF]/20" data-testid="nav-dashboard-mobile">
                <LayoutDashboard size={14} /> Dashboard
              </Link>
              <button onClick={() => { logout(); setMobileOpen(false); }} className="block text-sm text-slate-500 hover:text-white py-2 flex items-center gap-2"><LogOut size={14} /> Logout</button>
            </>
          ) : (
            <Link to="/signup" onClick={() => setMobileOpen(false)} className="block text-sm bg-[#31D65C] text-white px-5 py-2.5 rounded-full text-center font-bold shadow-lg shadow-[#31D65C]/20">Sign up</Link>
          )}
        </div>
      )}
      </div>
    </header>
  );
};
